import { useState, useRef, useCallback, useEffect } from 'react'
import { ModelPill } from './ModelPill'
import { CHAT_MODEL_CLAUDE_SONNET_45_SKILLS, DEFAULT_CHAT_MODEL_ID, type LlmConfigChatModelOption } from '../../lib/llmConfigModels'
import { SlashSkillsMenu, type SlashSkillMenuSelection } from './skills/SlashSkillsMenu.tsx'

export { MODEL_CLAUDE, MODEL_GPT4O, MODEL_GEMINI } from './ModelPill'

/** Minimal typing for webkit / standard speech recognition */
interface SpeechRecognitionLike {
  continuous: boolean
  lang: string
  interimResults: boolean
  onstart: (() => void) | null
  onend: (() => void) | null
  onresult: ((ev: SpeechRecognitionResultEvent) => void) | null
  start: () => void
  stop: () => void
}
interface SpeechRecognitionResultEvent {
  resultIndex: number
  results: {
    length: number
    [index: number]: { isFinal: boolean; 0: { transcript: string } }
  }
}

interface InputBarProps {
  onSendMessage: (content: string, file?: File, opts?: { useAgentSkills?: boolean; selectedSkillIds?: string[] }) => void
  disabled?: boolean
  isStreaming?: boolean
  onStop?: () => void
  chatModel?: string
  onChatModelChange?: (model: string) => void
  modelLocked?: boolean
  onToast?: (msg: string) => void
  chatModelOptions?: LlmConfigChatModelOption[]
}

export function InputBar({
  onSendMessage,
  disabled = false,
  isStreaming = false,
  onStop,
  chatModel = DEFAULT_CHAT_MODEL_ID,
  onChatModelChange = () => {},
  modelLocked = false,
  onToast,
  chatModelOptions,
}: InputBarProps) {
  const [value, setValue] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [pendingSkillSelections, setPendingSkillSelections] = useState<SlashSkillMenuSelection[]>([])
  const [slashMenuSuppressed, setSlashMenuSuppressed] = useState(false)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)

  const getLastToken = useCallback((text: string) => {
    const firstLine = (text ?? '').split('\n')[0] ?? ''
    const parts = firstLine.split(' ').filter(Boolean)
    return parts.length ? (parts[parts.length - 1] ?? '') : ''
  }, [])

  const autoResize = useCallback(() => {
    if (!taRef.current) return
    taRef.current.style.height = 'auto'
    taRef.current.style.height = Math.min(taRef.current.scrollHeight, 120) + 'px'
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    // Non-standard browser APIs (typed loosely)
    const SR = (window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike }).SpeechRecognition
      ?? (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognitionLike }).webkitSpeechRecognition
    if (!SR) return
    const rec = new SR()
    rec.continuous = true
    rec.lang = 'en-US'
    rec.interimResults = true
    rec.onstart = () => setIsRecording(true)
    rec.onend = () => setIsRecording(false)
    rec.onresult = (e: SpeechRecognitionResultEvent) => {
      let final = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript
      }
      if (final) { setValue(prev => (prev + ' ' + final).trim()); setTimeout(autoResize, 0) }
    }
    recognitionRef.current = rec
    return () => {
      try {
        rec.stop()
      } catch {
        void 0
      }
    }
  }, [autoResize])

  const toggleVoice = () => {
    const rec = recognitionRef.current
    if (!rec) { onToast?.('Voice input not supported in this browser'); return }
    if (isRecording) {
      rec.stop()
    } else {
      rec.start()
    }
  }

  const handleSend = useCallback(() => {
    const text = value.trim()
    if (!text || disabled) return
    const isSkillsModel = chatModel === CHAT_MODEL_CLAUDE_SONNET_45_SKILLS

    let useAgentSkills = false
    let selectedSkillIds: string[] | undefined
    if (isSkillsModel && text.startsWith('/')) {
      useAgentSkills = true
      if (pendingSkillSelections.length > 0) {
        selectedSkillIds = pendingSkillSelections.map(s => s.skillId)
      }
    } else if (isSkillsModel && pendingSkillSelections.length > 0) {
      useAgentSkills = true
      selectedSkillIds = pendingSkillSelections.map(s => s.skillId)
    }

    onSendMessage(text, selectedFile ?? undefined, (useAgentSkills || (selectedSkillIds && selectedSkillIds.length))
      ? { useAgentSkills: useAgentSkills || undefined, selectedSkillIds }
      : undefined)
    setValue('')
    setSelectedFile(null)
    setPendingSkillSelections([])
    if (taRef.current) taRef.current.style.height = 'auto'
  }, [value, disabled, onSendMessage, selectedFile, chatModel, pendingSkillSelections])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  return (
    <>
      <div className="input-area">
        <div className="input-wrap">
          {selectedFile && (
            <div className="file-chip">
              📎 {selectedFile.name}
              <button onClick={() => setSelectedFile(null)} aria-label="Remove file">×</button>
            </div>
          )}
          <div className="input-row">
            <div className={`input-box${isRecording ? ' voice-mode' : ''}`} style={{ position: 'relative' }}>
              <button type="button" className="icon-btn" aria-label="Attach file" title="Attach file" disabled={disabled} onClick={() => fileInputRef.current?.click()}>
                <svg viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M13 7L7.5 12.5A4.5 4.5 0 012 7.5L7.5 2A3 3 0 0112 6.5L6.5 12A1.5 1.5 0 014 10.5L10 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.mp4,.mov,.webm,.csv,.xlsx"
                onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
              />
              <textarea
                ref={taRef}
                className="input-ta"
                value={value}
                onChange={e => {
                  const next = e.target.value
                  setValue(next)
                  autoResize()
                  // If user leaves slash-skills mode entirely, clear selections.
                  if (!next.startsWith('/') && pendingSkillSelections.length) setPendingSkillSelections([])

                  // If user types a new "/" token, show the menu again.
                  const last = getLastToken(next)
                  if (last.startsWith('/')) setSlashMenuSuppressed(false)
                }}
                onKeyDown={handleKeyDown}
                placeholder="Message AIRA..."
                rows={1}
                aria-label="Message AIRA"
                disabled={disabled}
              />
              {chatModel === CHAT_MODEL_CLAUDE_SONNET_45_SKILLS && (
                <SlashSkillsMenu
                  value={value}
                  suppressed={slashMenuSuppressed}
                  excludeSkillIds={pendingSkillSelections.map(s => s.skillId)}
                  onPick={(sel) => {
                    setPendingSkillSelections(prev => {
                      if (prev.some(p => p.skillId === sel.skillId)) return prev
                      return [...prev, sel]
                    })
                    // Replace the current slash token with the selected skill name, then close the menu.
                    // User can type "/" again to add another skill.
                    setValue(prev => {
                      const text = prev ?? ''
                      const firstLine = text.split('\n')[0] ?? ''
                      const rest = text.slice(firstLine.length)
                      const tokens = firstLine.split(' ')
                      const lastIdx = tokens.length - 1
                      if (lastIdx >= 0 && (tokens[lastIdx] ?? '').startsWith('/')) {
                        tokens[lastIdx] = `/${sel.skillName}`
                      }
                      const rebuilt = tokens.filter(Boolean).join(' ')
                      return `${rebuilt} ${rest}`.replace(/\s+\n/g, '\n')
                    })
                    setSlashMenuSuppressed(true)
                    requestAnimationFrame(() => {
                      const el = taRef.current
                      if (!el) return
                      el.focus()
                      // Move caret to the end so the "typing indicator" (cursor) stays after tokens.
                      const end = el.value.length
                      try {
                        el.setSelectionRange(end, end)
                      } catch {
                        void 0
                      }
                    })
                  }}
                  disabled={disabled}
                />
              )}
              {/* Voice recording UI */}
              <div className="voice-recording-ui" aria-live="polite">
                <div className="wave-bars" aria-hidden="true">
                  {[0,1,2,3,4].map(i => <div key={i} className="wave-bar" style={{ animationDelay: `${i * 0.12}s` }} />)}
                </div>
                <div className="voice-status">Listening…</div>
              </div>
              <div className="input-acts">
                <ModelPill
                  value={chatModel}
                  onChange={onChatModelChange}
                  locked={modelLocked}
                  onLockedClick={() => onToast?.('Start a new chat to switch models')}
                  chatModelOptions={chatModelOptions}
                />
                {/* Mic button — hidden when streaming */}
                {!isStreaming && (
                  isRecording
                    ? <button type="button" className="stop-btn show" onClick={toggleVoice} disabled={disabled} aria-label="Stop recording">
                        <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><rect x="2" y="2" width="8" height="8" rx="1.5" fill="white"/></svg>
                      </button>
                    : <button type="button" className="mic-btn" onClick={toggleVoice} disabled={disabled} aria-label="Start voice input" title="Voice input">
                        <svg viewBox="0 0 15 15" fill="none" aria-hidden="true"><rect x="5" y="1" width="5" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.2"/><path d="M2.5 8A5 5 0 0012.5 8M7.5 13V11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                      </button>
                )}
                {/* Stop streaming / Send button */}
                {isStreaming ? (
                  <button className="stop-btn show" onClick={onStop} aria-label="Stop generating" title="Stop generating">
                    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true"><rect x="2" y="2" width="8" height="8" rx="1.5" fill="white"/></svg>
                  </button>
                ) : (
                  <button className="send-btn" onClick={handleSend} disabled={disabled || !value.trim()} aria-label="Send message">
                    <svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 11.5V2.5M3.5 6L7 2.5L10.5 6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="input-footer">Shift+Enter for new line &nbsp;·&nbsp; Responses may be inaccurate — verify with your team</div>
        </div>
      </div>
    </>
  )
}
